import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { requestsAPI, adminAPI } from '../../../services/api';
import InventoryStylePagination from '../../../components/InventoryStylePagination';
import { FiPlus, FiSearch, FiFilter, FiRefreshCw, FiAlertTriangle } from 'react-icons/fi';
import { LuEye, LuSquarePen } from 'react-icons/lu';
import { format } from 'date-fns';
import { statusColors } from '../../../utils/statusColors';
import { formatStatus } from '../../../utils/formatters';

const RequestsList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    companyId: '',
    dateRange: 'all'
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [processedCompanies, setProcessedCompanies] = useState([]);

  // Parse URL parameters and set initial filters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const typeParam = urlParams.get('type');

    if (typeParam) {
      const types = typeParam.split(',');
      setFilters(prev => ({
        ...prev,
        type: types.length === 1 ? types[0] : typeParam
      }));
    } else {
      setFilters(prev => ({ ...prev, type: '' }));
    }
    setPage(1);
  }, [location.search]);

  // Get page title based on URL filters
  const getPageTitle = () => {
    const urlParams = new URLSearchParams(location.search);
    const typeParam = urlParams.get('type');
    
    if (typeParam === 'incoming' || typeParam === 'incoming,crossdock' || typeParam === 'incoming,inbound_manifest' || typeParam === 'incoming,crossdock,inbound_manifest') {
      return 'Inbound Requests';
    } else if (typeParam === 'ship') {
      return 'Outbound Requests';
    } else if (typeParam && (typeParam.includes('pick_pack') || typeParam.includes('kit_build'))) {
      return 'Orders';
    }
    
    return 'All Requests';
  };

  // Get page description based on URL filters
  const getPageDescription = () => {
    const urlParams = new URLSearchParams(location.search);
    const typeParam = urlParams.get('type');
    
    if (typeParam === 'incoming' || typeParam === 'incoming,crossdock' || typeParam === 'incoming,inbound_manifest' || typeParam === 'incoming,crossdock,inbound_manifest') {
      return 'Incoming inventory, receiving requests (ASNs), inbound manifests, and cross-dock operations';
    } else if (typeParam === 'ship') {
      return 'Shipping and outbound fulfillment requests';
    } else if (typeParam && typeParam.includes('kit_build')) {
      return 'Kit building requests';
    }
    
    return 'A list of all service requests from companies including their status, type, and created date.';
  };

  // Fetch requests
  const { data, isLoading, isError, refetch } = useQuery(
    ['admin-requests', page, limit, searchTerm, filters],
    () => adminAPI.getRequests({
      page,
      limit,
      search: searchTerm,
      ...filters
    }),
    {
      keepPreviousData: true,
      onSuccess: (responseData) => {
      }
    }
  );

  // Fetch companies for filter
  const { data: companiesData, isLoading: isLoadingCompanies } = useQuery(
    ['companies-minimal'],
    () => adminAPI.getCustomers({ minimal: true }),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      onSuccess: (data) => {
      }
    }
  );

  // Process companies data when it changes
  useEffect(() => {
    if (companiesData) {
      let companies = [];
      
      if (companiesData.customers && Array.isArray(companiesData.customers)) {
        companies = companiesData.customers;
      } else if (companiesData.data && Array.isArray(companiesData.data)) {
        companies = companiesData.data;
      }
      
      setProcessedCompanies(companies);
    }
  }, [companiesData]);

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setFilters({
      ...filters,
      status: e.target.value
    });
    setPage(1);
  };

  // Handle type filter change
  const handleTypeFilterChange = (e) => {
    setFilters({
      ...filters,
      type: e.target.value
    });
    setPage(1);
  };

  // Handle company filter change
  const handleCompanyFilterChange = (e) => {
    setFilters({
      ...filters,
      companyId: e.target.value
    });
    setPage(1);
  };

  // Handle date range filter change
  const handleDateRangeFilterChange = (e) => {
    setFilters({
      ...filters,
      dateRange: e.target.value
    });
    setPage(1);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      status: '',
      type: '',
      companyId: '',
      dateRange: 'all'
    });
    setSearchTerm('');
    setPage(1);
  };

  // Get the request type display name
  const getRequestTypeName = (type) => {
    switch (type) {
      case 'ship':
        return 'Shipping';
      case 'incoming':
        return 'Incoming Inventory';
      case 'crossdock':
        return 'Cross-Dock';
      case 'inbound_manifest':
        return 'Inbound Manifest';
      case 'pick_pack':
        return 'Pick & Pack';
      case 'kit_build':
        return 'Kit Building';
      default:
        return type || 'Unknown';
    }
  };

  // Get company name from the request
  const getCompanyName = (request) => {
    // Check if request has a Company object directly
    if (request.Company) {
      return request.Company.name || 
             request.Company.companyName || 
             `Company ${request.companyId}`;
    }
    
    // Fall back to processed companies list if needed
    if (processedCompanies.length && request.companyId) {
      const company = processedCompanies.find(c => 
        c.id && request.companyId && c.id.toString() === request.companyId.toString()
      );
      
      return company ? (company.name || company.companyName) : 'Unknown';
    }
    
    return 'Unknown';
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">{getPageTitle()}</h1>
          <p className="mt-2 text-sm text-gray-700">
            {getPageDescription()}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            type="button"
            onClick={() => {
              const urlParams = new URLSearchParams(location.search);
              const typeParam = urlParams.get('type');
              
              if (typeParam === 'incoming' || typeParam === 'incoming,crossdock' || typeParam === 'incoming,inbound_manifest' || typeParam === 'incoming,crossdock,inbound_manifest') {
                navigate('/admin/requests/new?type=incoming');
              } else if (typeParam === 'ship') {
                navigate('/admin/requests/new?type=ship');
              } else {
                navigate('/admin/requests/new');
              }
            }}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <FiPlus className="mr-2" />
            New Request
          </button>
        </div>
      </div>

      {/* Debugging information */}
      {isError && (
        <div className="mt-4 p-4 border rounded-md bg-yellow-50 border-yellow-200">
          <div className="flex items-center">
            <FiAlertTriangle className="text-yellow-500 mr-2" />
            <h3 className="font-medium text-yellow-700">Troubleshooting Information</h3>
          </div>
          <div className="mt-2 text-sm text-yellow-600">
            <p>Request loading error. Check the console for details.</p>
            <p className="mt-1">Company data loaded: {processedCompanies.length} companies</p>
          </div>
        </div>
      )}

      {/* Search and filters */}
      <div className="mt-6 mb-8">
        {/* Search bar */}
        <div className="mb-4">
          <form onSubmit={handleSearch} className="relative rounded-md shadow-sm max-w-md">
            <input
              type="text"
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pr-10 sm:text-sm border-gray-300 rounded-md"
              placeholder="Search by ID, company name, manifest name, or notes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <FiSearch className="h-4 w-4 text-gray-400" />
            </div>
          </form>
        </div>
        
        {/* Filters row */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
          <div className="flex items-center">
            <FiFilter className="mr-2 text-gray-500 hidden sm:block" />
            <label htmlFor="status-filter" className="sr-only">Filter by status</label>
            <select
              id="status-filter"
              value={filters.status}
              onChange={handleStatusFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm w-full sm:w-auto"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <div className="flex items-center">
            <label htmlFor="type-filter" className="sr-only">Filter by type</label>
            <select
              id="type-filter"
              value={filters.type}
              onChange={handleTypeFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm w-full sm:w-auto"
            >
              <option value="">All Types</option>
              <option value="ship">Shipping</option>
              <option value="incoming">Incoming Inventory</option>
              <option value="inbound_manifest">Inbound Manifest</option>
              <option value="crossdock">Cross-Dock</option>
              <option value="kit_build">Kit Building</option>
            </select>
          </div>
          
          <div className="flex items-center">
            <label htmlFor="company-filter" className="sr-only">Filter by company</label>
            <select
              id="company-filter"
              value={filters.companyId}
              onChange={handleCompanyFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm w-full sm:w-auto"
            >
              <option value="">All Companies</option>
              {processedCompanies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name || company.companyName || `Company ${company.id}`}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center">
            <label htmlFor="date-filter" className="sr-only">Filter by date</label>
            <select
              id="date-filter"
              value={filters.dateRange}
              onChange={handleDateRangeFilterChange}
              className="focus:ring-indigo-500 focus:border-indigo-500 rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm w-full sm:w-auto"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
          </div>
          
          <div className="flex space-x-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={clearFilters}
              className="flex-1 sm:flex-none inline-flex items-center justify-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Clear
            </button>
            
            <button
              type="button"
              onClick={() => refetch()}
              className="flex-1 sm:flex-none inline-flex items-center justify-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <FiRefreshCw className="mr-1 h-3 w-3" /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Requests list */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
          <p className="ml-3 text-gray-500">Loading requests...</p>
        </div>
      ) : isError ? (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mt-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                There was an error loading requests. Please try again later.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md mt-4">
          {(!data?.data || data.data.length === 0) ? (
            <div className="px-4 py-5 sm:p-6 text-center">
              <p className="text-gray-500 text-base">No requests found.</p>
              <p className="text-gray-400 text-sm mt-1">Adjust your filters or create a new request.</p>
            </div>
          ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Request Number</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Company</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Type</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Created</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Last Updated</th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {data.data.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium sm:pl-6">
                      <Link to={`/admin/requests/${request.id}`} className="text-indigo-600 hover:text-indigo-900">
                        {request.requestNumber ? request.requestNumber.split('-').slice(0, 2).join('-') : (request.id?.substring(0, 8) || 'N/A')}
                      </Link>
                      {request.requestType === 'inbound_manifest' && request.manifestName && (
                        <div className="mt-1 text-xs font-normal text-gray-500">
                          Manifest Name: {request.manifestName}
                        </div>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {getCompanyName(request)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {getRequestTypeName(request.requestType)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[request.status] || 'bg-gray-100 text-gray-800'}`}>
                        {formatStatus(request.status, request.requestType)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {request.createdAt ? format(new Date(request.createdAt), 'MMM d, yyyy') : 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {request.updatedAt ? format(new Date(request.updatedAt), 'MMM d, yyyy') : 'N/A'}
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <div className="flex items-center justify-end space-x-3">
                        <Link
                          to={`/admin/requests/${request.id}`}
                          className="text-gray-400 hover:text-gray-600"
                          aria-label="View request"
                        >
                          <LuEye className="h-4 w-4" />
                        </Link>
                        <button
                          onClick={() => navigate(`/admin/requests/${request.id}/edit`)}
                          className="text-gray-400 hover:text-gray-600"
                          aria-label="Edit request"
                        >
                          <LuSquarePen className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden p-4 space-y-4">
            {data.data.map((request) => (
              <div key={request.id} className="bg-white shadow rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-medium text-gray-900">
                      {request.requestNumber ? request.requestNumber.split('-').slice(0, 2).join('-') : (request.id?.substring(0, 8) || 'N/A')}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[request.status] || 'bg-gray-100 text-gray-800'}`}>
                      {formatStatus(request.status, request.requestType)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/admin/requests/${request.id}`}
                      className="text-indigo-600 hover:text-indigo-900 p-1"
                    >
                      <LuEye className="h-4 w-4" />
                    </Link>
                    <button
                      onClick={() => navigate(`/admin/requests/${request.id}/edit`)}
                      className="text-gray-400 hover:text-gray-600 p-1"
                    >
                      <LuSquarePen className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                
                  <div className="space-y-2 text-sm">
                  {request.requestType === 'inbound_manifest' && request.manifestName && (
                    <div className="flex justify-between gap-4">
                      <span className="text-gray-500">Manifest:</span>
                      <span className="text-right text-gray-900">{request.manifestName}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Company:</span>
                    <span className="text-gray-900 font-medium">{getCompanyName(request)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Type:</span>
                    <span className="text-gray-900">{getRequestTypeName(request.requestType)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Created:</span>
                    <span className="text-gray-900">{request.createdAt ? format(new Date(request.createdAt), 'MMM d, yyyy') : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Updated:</span>
                    <span className="text-gray-900">{request.updatedAt ? format(new Date(request.updatedAt), 'MMM d, yyyy') : 'N/A'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
          )}

          <InventoryStylePagination
            currentPage={page}
            totalPages={data?.totalPages || 1}
            totalItems={data?.total || 0}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={(nextLimit) => {
              setLimit(nextLimit);
              setPage(1);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default RequestsList; 
